/**
 * PCM16 mono to a WAV file, for dictation's upload.
 *
 * Dictation records with `react-native-audio-api`, which hands over samples,
 * not a file; the transcriber takes a file. A WAV is the smallest honest one:
 * a 44-byte RIFF header in front of the very bytes the recorder produced, so
 * nothing is encoded on the phone and nothing is lost. Pure, so the header's
 * every field is proven by a test rather than by a transcriber that shrugs.
 */

/** The header's length: RIFF, fmt and data chunk headers, with no extras. */
export const WAV_HEADER_BYTES = 44;

/**
 * One WAV file's bytes from PCM16 little-endian mono chunks at `rate`.
 *
 * The chunks are joined in order. A total with an odd byte loses it, because
 * half a sample is not a sample and a WAV whose data is not whole samples is
 * refused by stricter readers.
 */
export const pcm16Wav = (chunks: readonly Uint8Array[], rate: number): Uint8Array<ArrayBuffer> => {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const dataBytes = total - (total % 2);
  const out = new Uint8Array(WAV_HEADER_BYTES + dataBytes);
  const view = new DataView(out.buffer);
  const ascii = (at: number, text: string): void => {
    for (let index = 0; index < text.length; index += 1) out[at + index] = text.charCodeAt(index);
  };

  const channels = 1;
  const bytesPerSample = 2;
  ascii(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true);
  // 1 is integer PCM.
  view.setUint16(20, 1, true);
  view.setUint16(22, channels, true);
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * channels * bytesPerSample, true);
  view.setUint16(32, channels * bytesPerSample, true);
  view.setUint16(34, bytesPerSample * 8, true);
  ascii(36, 'data');
  view.setUint32(40, dataBytes, true);

  let at = WAV_HEADER_BYTES;
  for (const chunk of chunks) {
    const room = WAV_HEADER_BYTES + dataBytes - at;
    if (room <= 0) break;
    out.set(chunk.length <= room ? chunk : chunk.subarray(0, room), at);
    at += Math.min(chunk.length, room);
  }
  return out;
};
